/** Login page for admin authentication. */
import React, { useState } from "react";
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../api/client";

/** Login page component. */
export default function Login() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const expired = searchParams.get("expired") === "1";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /** Dismiss the session-expired banner. */
  const dismissExpired = () => {
    searchParams.delete("expired");
    setSearchParams(searchParams, { replace: true });
  };

  /** Toggle password visibility (UX review B4). */
  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  /** Handle form submission. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Please enter both username and password");
      return;
    }

    setLoading(true);

    try {
      // Login and get token
      const response = await authApi.login(username, password);
      // Store token in localStorage as fallback (cookie should also be set)
      if (response.data.access_token) {
        localStorage.setItem("access_token", response.data.access_token);
      }
      localStorage.setItem("isAuthenticated", "true");
      // RBAC (staff roles): remember the actor role for nav filtering.
      localStorage.setItem("auth_role", response.data?.role || "admin");
      dismissExpired();
      // A1 (GAP V2): forced credential rotation — default/seeded credentials
      // dead-end here instead of reaching the dashboard.
      if (response.data?.must_change_password) {
        navigate("/change-password");
        return;
      }
      navigate("/dashboard");
    } catch (err: any) {
      const detail = err.response?.data?.detail || "";
      setError(
        typeof detail === "string" && detail
          ? detail
          : "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: "primary.main",
                color: "white",
                mb: 2,
              }}
            >
              <LockIcon fontSize="large" />
            </Box>
            <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
              Softswitch Control Plane
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access the dashboard
            </Typography>
          </Box>

          {expired && (
            <Alert severity="warning" sx={{ mb: 3 }} onClose={dismissExpired}>
              Your session has expired. Please sign in again.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={handleTogglePassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
